import { useEffect, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { FaArrowLeft, FaChevronLeft, FaChevronRight, FaSearch, FaSyncAlt } from "react-icons/fa";
import { categoryPaths, getPhysicalCategoryPage, type PhysicalCategory, type PhysicalCategoryAttribute, type PhysicalCategoryValue, type PhysicalObjectCategory } from "@/api/physicalRecordsCategoriesApi";
import type { PagedResult } from "@/api/physicalRecordsApi";
import { categoryReadError, categoryValueFields, groupCategoryValues, presentCategoryValues } from "@/utils/physicalRecordsCategories";
import "./PhysicalRecordCategories.scss";

function useCategoryPage<T>(path: string, page: number, search = "") {
  const [state, setState] = useState<{ key: string; data?: PagedResult<T>; error?: ReturnType<typeof categoryReadError> }>({ key: "" });
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify([path, page, search, attempt]);
  useEffect(() => {
    const controller = new AbortController();
    getPhysicalCategoryPage<T>(path, page, search, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setState({ key, data }); })
      .catch((error: unknown) => { if (!controller.signal.aborted) setState({ key, error: categoryReadError(error) }); });
    return () => controller.abort();
  }, [path, page, search, key]);
  return { data: state.key === key ? state.data : undefined, error: state.key === key ? state.error : undefined,
    loading: state.key !== key, retry: () => setAttempt((current) => current + 1) };
}

function PageControls({ data, onChange }: { data: PagedResult<unknown>; onChange: (page: number) => void }) {
  const pages = Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  return <div className="physical-category-pagination">
    <span>{data.totalCount.toLocaleString()} items · Page {data.page} of {pages}</span>
    <div className="btn-group btn-group-sm" role="group" aria-label="Category pagination">
      <Button variant="outline-secondary" disabled={data.page <= 1} onClick={() => onChange(data.page - 1)} title="Previous page" aria-label="Previous page"><FaChevronLeft /></Button>
      <Button variant="outline-secondary" disabled={data.page >= pages} onClick={() => onChange(data.page + 1)} title="Next page" aria-label="Next page"><FaChevronRight /></Button>
    </div>
  </div>;
}

function ReadState({ loading, error, retry }: { loading: boolean; error?: ReturnType<typeof categoryReadError>; retry: () => void }) {
  if (loading) return <div role="status" className="py-3"><Spinner size="sm" className="me-2" />Loading categories...</div>;
  if (!error) return null;
  return <Alert variant={error.missingSchema ? "warning" : "danger"}>
    <div>{error.missingSchema && <strong>Categories unavailable. </strong>}{error.message}</div>
    <Button className="mt-2" size="sm" variant="outline-secondary" onClick={retry}><FaSyncAlt aria-hidden="true" /> Retry</Button>
  </Alert>;
}

export function CategoryDefinitionsBrowser() {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<PhysicalCategory | null>(null);
  const result = useCategoryPage<PhysicalCategory>(categoryPaths.definitions, page, appliedSearch);
  if (selected) return <CategoryAttributes key={selected.categoryId} category={selected} onBack={() => setSelected(null)} />;
  return <section className="physical-categories" aria-label="Category definitions">
    <Form className="physical-category-search mb-3" onSubmit={(event) => { event.preventDefault(); setAppliedSearch(search.trim()); setPage(1); }}>
      <Form.Control aria-label="Search category definitions" placeholder="Category name" maxLength={512} value={search} onChange={(event) => setSearch(event.target.value)} />
      <Button type="submit" title="Search categories" aria-label="Search categories"><FaSearch /></Button>
      <Button variant="outline-secondary" onClick={() => { setSearch(""); setAppliedSearch(""); setPage(1); }}>Clear</Button>
    </Form>
    <ReadState {...result} />
    {result.data && <>
      <div className="table-responsive"><table className="table table-sm table-hover align-middle physical-category-table"><thead><tr><th>Category</th><th>Legacy ID</th><th>Source objects</th><th>Source rows</th><th><span className="visually-hidden">Attributes</span></th></tr></thead>
        <tbody>{result.data.items.map((category) => <tr key={category.categoryId}>
          <td><button type="button" className="btn btn-link p-0 text-start" onClick={() => setSelected(category)}>{category.categoryName}</button></td>
          <td>{category.legacyCategoryId}</td><td>{category.sourceObjectCount.toLocaleString()}</td><td>{category.sourceAttributeRowCount.toLocaleString()}</td>
          <td><Button size="sm" variant="outline-secondary" title={`Browse ${category.categoryName} attributes`} aria-label={`Browse ${category.categoryName} attributes`} onClick={() => setSelected(category)}><FaChevronRight /></Button></td>
        </tr>)}</tbody></table></div>
      {result.data.items.length === 0 && <p className="text-muted" role="status">{appliedSearch ? "No categories match your search." : "No category definitions are available."}</p>}
      <PageControls data={result.data} onChange={setPage} />
    </>}
  </section>;
}

function CategoryAttributes({ category, onBack }: { category: PhysicalCategory; onBack: () => void }) {
  const [page, setPage] = useState(1);
  const result = useCategoryPage<PhysicalCategoryAttribute>(categoryPaths.attributes(category.categoryId), page);
  return <section className="physical-categories" aria-label="Category attributes">
    <Button size="sm" variant="outline-secondary" className="mb-3" onClick={onBack}><FaArrowLeft aria-hidden="true" /> Categories</Button>
    <h2 className="h5 text-break">{category.categoryName}</h2><p className="small text-muted">Legacy ID: {category.legacyCategoryId}</p>
    <ReadState {...result} />
    {result.data && <>
      <div className="table-responsive"><table className="table table-sm physical-category-table"><thead><tr><th>Region</th><th>Set</th><th>Attribute</th><th>Legacy attribute ID</th><th>Source type</th><th>Reporting level</th></tr></thead>
        <tbody>{result.data.items.map((attribute) => <tr key={attribute.categoryAttributeId}><td>{attribute.regionName}</td><td>{attribute.setName ?? "-"}</td><td>{attribute.attributeName}</td><td>{attribute.legacyAttributeId}</td><td>{attribute.sourceAttributeType ?? "-"}</td><td>{attribute.reportingLevel ?? "-"}</td></tr>)}</tbody></table></div>
      {result.data.items.length === 0 && <p className="text-muted" role="status">No attributes are defined for this category.</p>}
      <PageControls data={result.data} onChange={setPage} />
    </>}
  </section>;
}

export function PhysicalObjectCategories({ objectId }: { objectId: number }) {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<PhysicalObjectCategory | null>(null);
  const result = useCategoryPage<PhysicalObjectCategory>(categoryPaths.assignments(objectId), page);
  const activeAssignment = result.data?.items.find((item) => item.objectCategoryId === selected?.objectCategoryId)
    ?? result.data?.items[0];
  return <section className="physical-workspace-view physical-categories" aria-label="Object categories">
    <ReadState {...result} />
    {result.data && <>
      {result.data.items.length === 0 ? <p className="text-muted" role="status">No categories are assigned to this record.</p> : <nav className="physical-category-selector" aria-label="Assigned categories">
        {result.data.items.map((assignment) => <Button key={assignment.objectCategoryId}
          variant="outline-secondary"
          className="physical-category-pill"
          aria-pressed={activeAssignment?.objectCategoryId === assignment.objectCategoryId} onClick={() => setSelected(assignment)}>
          {assignment.categoryName}
          {(result.data?.items.filter((item) => item.legacyCategoryId === assignment.legacyCategoryId).length ?? 0) > 1
            && <span className="d-block small">Definition {assignment.definitionVersion} / Object {assignment.objectVersion}</span>}
        </Button>)}
      </nav>}
      {result.data.totalCount > result.data.pageSize && <PageControls data={result.data} onChange={(next) => { setSelected(null); setPage(next); }} />}
      {activeAssignment && <CategoryValues key={`${objectId}:${activeAssignment.objectCategoryId}`} objectId={objectId} assignment={activeAssignment} />}
    </>}
  </section>;
}

function CategoryValues({ objectId, assignment }: { objectId: number; assignment: PhysicalObjectCategory }) {
  const [page, setPage] = useState(1);
  const result = useCategoryPage<PhysicalCategoryValue>(categoryPaths.values(objectId, assignment.objectCategoryId), page);
  return <section className="physical-category-values" aria-label="Assigned category values">
    <h3 className="h5 text-break">{assignment.categoryName}</h3>
    <ReadState {...result} />
    {result.data && <>
      {presentCategoryValues(result.data.items, assignment.categoryName).map((group) => <section key={group.key} className="physical-category-form-group">
        {group.label && <h4 className="h6">{group.label}</h4>}
        <dl className="physical-category-readable-fields">{group.rows.map((value) => {
          const fields = categoryValueFields(value);
          return <div key={value.categoryValueId}>
            <dt>{value.attributeName ?? `Attribute ${value.legacyAttributeId}`}</dt>
            <dd>{fields.length === 0 ? <span className="text-muted">Not provided</span>
              : fields.map(([label, content]) => <span className="physical-category-readable-value" key={label}>
                {fields.length > 1 && <span className="small text-muted">{label}: </span>}{content}
              </span>)}</dd>
          </div>;
        })}</dl>
      </section>)}
      {result.data.items.length === 0 && <p className="text-muted" role="status">No attribute values are available for this assignment.</p>}
      {result.data.totalCount > result.data.pageSize && <PageControls data={result.data} onChange={setPage} />}
      <details className="physical-category-import-details">
        <summary>Import details</summary>
        <p className="small text-muted mt-2">Definition version {assignment.definitionVersion} · Object version {assignment.objectVersion} · Legacy ID {assignment.legacyCategoryId}</p>
      {groupCategoryValues(result.data.items).map((group) => <section key={group.key} className="physical-category-group">
        <h4>{group.label}</h4>
        <div className="table-responsive"><table className="table table-sm physical-category-table"><thead><tr><th>Attribute</th><th>Value</th></tr></thead><tbody>{group.rows.map((value) => <tr key={value.categoryValueId}>
          <th scope="row">{value.attributeName ?? `Attribute ${value.legacyAttributeId}`}<span className="d-block small fw-normal text-muted">ID {value.legacyAttributeId} · Type {value.sourceAttributeType ?? "-"} · Row {value.categoryValueId}</span></th>
          <td>{categoryValueFields(value).length === 0 ? <span className="text-muted">No stored value</span> : <dl className="physical-category-value-fields">{categoryValueFields(value).map(([label, content]) => <div key={label}><dt>{label}</dt><dd>{content}</dd></div>)}</dl>}</td>
        </tr>)}</tbody></table></div>
      </section>)}
      </details>
    </>}
  </section>;
}